import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComboboxOption } from './Combobox.types.ts';

interface UseComboboxControllerInput<TValue extends string> {
    label?: string;
    ariaLabel?: string;
    options: ComboboxOption<TValue>[];
    value?: TValue | null;
    disabled: boolean;
    filterOption?: (option: ComboboxOption<TValue>, query: string) => boolean;
    onChange: (value: TValue, option: ComboboxOption<TValue>) => void;
}

export function useComboboxController<TValue extends string>({
    label,
    ariaLabel,
    options,
    value,
    disabled,
    filterOption,
    onChange,
}: UseComboboxControllerInput<TValue>) {
    const generatedId = React.useId();
    const triggerId = `${generatedId}-trigger`;
    const listboxId = `${generatedId}-listbox`;
    const rootRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);

    const selectedOption = useMemo(
        () => options.find(option => option.value === value) ?? null,
        [options, value]
    );
    const filteredOptions = useMemo(
        () => filterComboboxOptions(options, query, filterOption),
        [filterOption, options, query]
    );
    const enabled = !disabled && options.length > 0;
    const controlName = ariaLabel ?? label ?? 'Combobox';
    const activeOptionIndex = useMemo(
        () => resolveActiveOptionIndex(filteredOptions, activeIndex, value),
        [activeIndex, filteredOptions, value]
    );
    const activeOption = activeOptionIndex >= 0 ? filteredOptions[activeOptionIndex] : undefined;
    const activeOptionId = activeOptionIndex >= 0 ? `${listboxId}-option-${activeOptionIndex}` : undefined;
    const close = useComboboxClose({ setActiveIndex, setIsOpen, setQuery, triggerRef });
    const open = useCallback(() => enabled && setIsOpen(true), [enabled]);
    const moveActive = useComboboxActiveMover(filteredOptions, activeOptionIndex, setActiveIndex);
    const selectOption = useComboboxSelectOption(onChange, close);

    useFocusSearchOnOpen(isOpen, searchInputRef);
    useOutsideMouseDismiss(isOpen, rootRef, popoverRef, close);
    const handlePopoverKeyDown = useComboboxPopoverKeyDown({
        activeOption,
        close,
        moveActive,
        selectOption,
    });
    const handleRootBlur = useComboboxRootBlur(isOpen, popoverRef, close);

    return {
        activeOptionId,
        activeOptionIndex,
        close,
        controlName,
        enabled,
        filteredOptions,
        handlePopoverKeyDown,
        handleRootBlur,
        ids: { listboxId, triggerId },
        isOpen,
        query,
        refs: { popoverRef, rootRef, searchInputRef, triggerRef },
        selectedOption,
        selectOption,
        setActiveIndex,
        setQuery,
        open,
    };
}

function filterComboboxOptions<TValue extends string>(
    options: ComboboxOption<TValue>[],
    query: string,
    filterOption?: (option: ComboboxOption<TValue>, query: string) => boolean
): ComboboxOption<TValue>[] {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter(option => {
        if (filterOption) return filterOption(option, query);

        const haystack = [
            option.label,
            option.description,
            option.meta,
            ...(option.keywords ?? []),
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalizedQuery);
    });
}

function resolveActiveOptionIndex<TValue extends string>(
    options: ComboboxOption<TValue>[],
    activeIndex: number,
    selectedValue: TValue | null | undefined
): number {
    const activeOption = options[activeIndex];
    if (activeOption && !activeOption.disabled) return activeIndex;

    const selectedIndex = options.findIndex(option => option.value === selectedValue && !option.disabled);
    if (selectedIndex >= 0) return selectedIndex;

    return options.findIndex(option => !option.disabled);
}

function resolveNextActiveOptionIndex<TValue extends string>(
    options: ComboboxOption<TValue>[],
    activeOptionIndex: number,
    direction: 1 | -1
): number {
    if (options.length === 0) return -1;

    const candidateIndexes = options.map((_, step) => {
        if (activeOptionIndex < 0) {
            return direction > 0 ? step : options.length - 1 - step;
        }
        return (activeOptionIndex + direction * (step + 1) + options.length) % options.length;
    });
    return candidateIndexes.find(index => !options[index]?.disabled) ?? -1;
}

function useComboboxClose({
    setActiveIndex,
    setIsOpen,
    setQuery,
    triggerRef,
}: {
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setQuery: React.Dispatch<React.SetStateAction<string>>;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
}) {
    return useCallback((restoreFocus = false) => {
        setIsOpen(false);
        setQuery('');
        setActiveIndex(-1);
        if (restoreFocus) triggerRef.current?.focus();
    }, [setActiveIndex, setIsOpen, setQuery, triggerRef]);
}

function useComboboxActiveMover<TValue extends string>(
    filteredOptions: ComboboxOption<TValue>[],
    activeOptionIndex: number,
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>
) {
    return useCallback((direction: 1 | -1) => {
        const nextIndex = resolveNextActiveOptionIndex(filteredOptions, activeOptionIndex, direction);
        if (nextIndex >= 0) setActiveIndex(nextIndex);
    }, [activeOptionIndex, filteredOptions, setActiveIndex]);
}

function useComboboxSelectOption<TValue extends string>(
    onChange: (value: TValue, option: ComboboxOption<TValue>) => void,
    close: (restoreFocus?: boolean) => void
) {
    return useCallback((option: ComboboxOption<TValue>) => {
        if (option.disabled) return;
        onChange(option.value, option);
        close(true);
    }, [close, onChange]);
}

function useFocusSearchOnOpen(
    isOpen: boolean,
    searchInputRef: React.RefObject<HTMLInputElement | null>
) {
    useEffect(() => {
        if (!isOpen) return;
        searchInputRef.current?.focus();
    }, [isOpen, searchInputRef]);
}

function useOutsideMouseDismiss(
    isOpen: boolean,
    rootRef: React.RefObject<HTMLDivElement | null>,
    popoverRef: React.RefObject<HTMLDivElement | null>,
    close: () => void
) {
    useEffect(() => {
        if (!isOpen) return;

        const handleDocumentMouseDown = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) {
                return;
            }
            if (rootRef.current?.contains(event.target) || popoverRef.current?.contains(event.target)) {
                return;
            }
            close();
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
    }, [close, isOpen, popoverRef, rootRef]);
}

function useComboboxPopoverKeyDown<TValue extends string>({
    activeOption,
    close,
    moveActive,
    selectOption,
}: {
    activeOption: ComboboxOption<TValue> | undefined;
    close: (restoreFocus?: boolean) => void;
    moveActive: (direction: 1 | -1) => void;
    selectOption: (option: ComboboxOption<TValue>) => void;
}) {
    return useCallback((event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            moveActive(event.key === 'ArrowDown' ? 1 : -1);
            return;
        }
        if (event.key === 'Enter' && activeOption && !activeOption.disabled) {
            event.preventDefault();
            selectOption(activeOption);
        }
    }, [activeOption, close, moveActive, selectOption]);
}

function useComboboxRootBlur(
    isOpen: boolean,
    popoverRef: React.RefObject<HTMLDivElement | null>,
    close: () => void
) {
    return useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        if (!isOpen) return;

        const nextFocusTarget = event.relatedTarget;
        if (nextFocusTarget instanceof Node && event.currentTarget.contains(nextFocusTarget)) return;
        if (nextFocusTarget instanceof Node && popoverRef.current?.contains(nextFocusTarget)) return;

        close();
    }, [close, isOpen, popoverRef]);
}
